import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Header from '../components/site/Header';
import Footer from '../components/site/Footer';
import { useQuoteModal } from '../contexts/QuoteModalContext';
import { useBlogBySlug, useBlogs } from '../hooks/useBlogs';
import useSeo from '../hooks/useSeo';
import NotFound from './NotFound';
import RelatedReads from '../components/site/RelatedReads';
import ArticleBlock from '../components/site/ArticleBlock';

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const { openModal } = useQuoteModal();
  const { post, loading } = useBlogBySlug(slug);
  const { data: allPosts } = useBlogs();

  useSeo({
    title: post ? `${post.title} | Pusha Teez` : '',
    description: post?.excerpt,
    path: `/blog/${slug}`,
    image: post?.image,
    type: 'article',
    keywords: post?.keywords?.join(', '),
  });

  // JSON-LD structured data
  useEffect(() => {
    if (!post) return undefined;
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      image: [new URL(post.image || '/og-image.jpg', window.location.origin).href],
      datePublished: post.publishedAt,
      author: { '@type': 'Organization', name: 'Pusha Teez' },
      publisher: {
        '@type': 'Organization',
        name: 'Pusha Teez',
        logo: {
          '@type': 'ImageObject',
          url: 'https://customer-assets.emergentagent.com/job_pusha-rebrand/artifacts/utn0sdo9_PUSHA_TEE_GLOW_LOGO.png',
        },
      },
      keywords: post.keywords.join(', '),
      articleSection: post.category,
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    script.setAttribute('data-blog-jsonld', 'true');
    document.head.appendChild(script);

    return () => {
      const old = document.querySelector('script[data-blog-jsonld="true"]');
      if (old) old.remove();
    };
  }, [post]);

  if (loading && !post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-[#cfc8bb]">
        <span className="text-sm uppercase tracking-[0.3em]">Loading…</span>
      </div>
    );
  }
  if (!post) {
    return <NotFound />;
  }

  return (
    <div className="bg-black min-h-screen">
      <Header />
      <main className="pt-32 pb-24">
        <article className="max-w-[860px] mx-auto px-6">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#cfc8bb] text-sm font-medium hover:text-[#f4efe6] transition-colors duration-200 group mb-10"
          >
            <ArrowLeft className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-1" />
            Back to all articles
          </Link>

          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-[#00b8ff] mb-5">
            {[
              { t: post.category, c: '' },
              { t: post.readTime, c: 'text-[#cfc8bb]' },
              { t: post.publishedAt && formatDate(post.publishedAt), c: 'text-[#cfc8bb] normal-case tracking-normal' },
            ]
              .filter((m) => m.t)
              .map((m, i) => (
                <React.Fragment key={m.t}>
                  {i > 0 && <span className="w-1 h-1 rounded-full bg-[#00b8ff]/60" />}
                  <span className={m.c}>{m.t}</span>
                </React.Fragment>
              ))}
          </div>

          <h1 className="font-display text-[#f4efe6] uppercase text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight mb-8">
            {post.title}
          </h1>

          <p className="text-[#cfc8bb] text-lg md:text-xl leading-relaxed mb-10">
            {post.excerpt}
          </p>

          <div className="relative overflow-hidden rounded-md aspect-[16/9] mb-12 shadow-2xl">
            <img
              src={post.image}
              alt={post.imageAlt}
              className="w-full h-full object-cover"
              loading="eager"
              decoding="async"
            />
          </div>

          <div className="text-[#dcd6c9] text-base md:text-lg leading-[1.75]">
            {post.content.map((block, i) => (
              <ArticleBlock key={`${post.slug}-${block.type}-${i}`} block={block} fallbackAlt={post.title} />
            ))}
          </div>

          {/* Inline CTA */}
          <div className="mt-14 p-8 md:p-10 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01]">
            <h3 className="font-display text-[#f4efe6] uppercase text-3xl md:text-4xl tracking-tight mb-3">
              Ready to <span className="font-script text-[#00b8ff] normal-case italic">activate</span>?
            </h3>
            <p className="text-[#cfc8bb] mb-6 max-w-2xl">
              Tell us about your event and we&apos;ll design a live merch printing
              activation tailored to your goals, audience, and budget.
            </p>
            <button
              type="button"
              onClick={openModal}
              className="inline-flex items-center gap-2 bg-[#00b8ff] text-black rounded-full px-7 py-3.5 font-semibold text-[15px] hover:bg-[#f4efe6] transition-colors duration-200 group"
            >
              Get a Quote
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </div>
        </article>

        <RelatedReads post={post} allPosts={allPosts} />
      </main>
      <Footer />
    </div>
  );
}
